import { useState } from 'react'
import { Cargando } from '../../componentes/Cargando'
import { EstadoError } from '../../componentes/EstadoError'
import { IconoFactura, IconoVolver } from '../../componentes/iconos'
import type { ClienteResumen } from '../../hooks/useClientes'
import { MapaCliente } from './MapaCliente'
import { useClienteDetalle, useGeocodificar, useUltimaFactura, type ClienteDetalle } from './consultas'

interface Props {
  /** Fila seleccionada: da nombre al panel antes de que llegue la ficha. */
  cliente: ClienteResumen
  onVolver: () => void
}

/** Direccion en lineas, saltandose los huecos que Holded deja vacios. */
function lineasDireccion(ficha: ClienteDetalle): string[] {
  const poblacion = [ficha.codigo_postal, ficha.ciudad].filter(Boolean).join(' ')
  const region = [ficha.provincia, ficha.pais].filter(Boolean).join(', ')
  return [ficha.direccion ?? '', poblacion, region]
    .map((linea) => linea.trim())
    .filter((linea) => linea.length > 0)
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">{etiqueta}</dt>
      <dd className="mt-0.5 text-sm text-tinta-2">{valor}</dd>
    </div>
  )
}

/**
 * Ficha del cliente seleccionado. Cada accion (geocodificar, ultima factura)
 * tiene su propio estado de carga y de error y ninguna arrastra a las demas:
 * en la version Streamlit un fallo de Holded tumbaba la pagina entera.
 */
export function PanelCliente({ cliente, onVolver }: Props) {
  const detalle = useClienteDetalle(cliente.id)
  const geocodificar = useGeocodificar(cliente.id)
  const [verFactura, setVerFactura] = useState(false)
  const factura = useUltimaFactura(cliente.id, verFactura)

  const ficha = detalle.data
  const nombre = ficha?.nombre ?? cliente.nombre

  return (
    <section
      aria-label="Detalle del cliente"
      className="flex flex-col gap-4 rounded-xl border border-borde bg-superficie p-4"
    >
      <header className="flex items-start gap-2">
        {/* Solo en movil: en escritorio el panel esta al lado de la tabla y no
            hay nada de lo que "volver". */}
        <button
          type="button"
          onClick={onVolver}
          className="-ml-1 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado lg:hidden"
        >
          <IconoVolver aria-hidden="true" className="size-4" />
          <span>Volver al listado</span>
        </button>
        <h2 className="min-w-0 flex-1 text-lg font-semibold text-tinta lg:text-xl">{nombre}</h2>
      </header>

      {detalle.isPending ? (
        <Cargando mensaje="Cargando la ficha del cliente" />
      ) : detalle.isError ? (
        <EstadoError error={detalle.error} onReintentar={() => void detalle.refetch()} />
      ) : (
        <>
          <dl className="grid gap-3">
            <Dato etiqueta="Movil" valor={ficha?.movil ?? 'Sin movil registrado'} />
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">
                Direccion
              </dt>
              <dd className="mt-0.5 text-sm text-tinta-2">
                {ficha && lineasDireccion(ficha).length > 0 ? (
                  lineasDireccion(ficha).map((linea) => <span key={linea} className="block">{linea}</span>)
                ) : (
                  <span>Sin direccion registrada</span>
                )}
              </dd>
            </div>
          </dl>

          {ficha?.etiquetas && ficha.etiquetas.length > 0 ? (
            <ul aria-label="Etiquetas de reparto" className="flex flex-wrap gap-2">
              {ficha.etiquetas.map((etiqueta) => (
                <li
                  key={etiqueta}
                  className="rounded-full bg-elevado px-2.5 py-1 text-xs font-medium text-tinta-2"
                >
                  {etiqueta}
                </li>
              ))}
            </ul>
          ) : null}

          <MapaCliente
            nombre={nombre}
            latitud={ficha?.latitud}
            longitud={ficha?.longitud}
            onGeocodificar={() => geocodificar.mutate()}
            geocodificando={geocodificar.isPending}
            error={geocodificar.error}
          />
        </>
      )}

      <div className="border-t border-borde-suave pt-4">
        <h3 className="text-sm font-semibold text-tinta">Ultima factura</h3>
        {!verFactura ? (
          <button
            type="button"
            onClick={() => setVerFactura(true)}
            className="mt-2 inline-flex items-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
          >
            <IconoFactura aria-hidden="true" className="size-4" />
            <span>Ver ultima factura</span>
          </button>
        ) : factura.isPending ? (
          <Cargando mensaje="Consultando la ultima factura" />
        ) : factura.isError ? (
          // Aislado a proposito: el mapa y la tabla de arriba siguen en pie.
          <div className="mt-2">
            <EstadoError error={factura.error} onReintentar={() => void factura.refetch()} />
          </div>
        ) : (
          <p className="mt-2 text-sm text-tinta-2">{factura.data?.resumen}</p>
        )}
      </div>
    </section>
  )
}
